'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import Image from 'next/image';
import { useT } from '@/components/i18n-provider';
import { Send } from 'lucide-react';
import { Avatar } from '@/components/avatar';
import { Spinner } from '@/components/ui/spinner';
import { useSignupGate } from '@/components/workspace/signup-gate';
import { useWorkspaceAnon } from '@/components/workspace/anon-context';
import { haptic } from '@/lib/haptic';
import { cn } from '@/lib/utils';

/**
 * Read-only preview of an agent chat for anonymous visitors.
 *
 * Drops the user into the agent's welcome screen (greeting + suggested
 * actions + textarea) without requiring a session. The textarea is
 * fully functional locally; pressing Enter or clicking Send opens the
 * signup gate with the typed text stashed in `localStorage` so the
 * post-signup clone-from flow can start a real chat with that prompt
 * as the first message.
 *
 * Suggested actions behave the same way: tapping one fills the input
 * (so the visitor can edit) and then submitting carries it through.
 */
export function AnonAgentChatPreview({
  username,
  prefillText,
}: {
  username: string;
  prefillText?: string;
}) {
  const t = useT();
  const { requireSignup } = useSignupGate();
  const { sourceWorkspaceId } = useWorkspaceAnon();
  const [greeting, setGreeting] = useState<{
    displayName?: string;
    avatarUrl?: string;
    greetingMessage?: string;
    greetingPrompt?: string;
    suggestedActions?: string[];
    heroImage?: string;
  } | null>(null);
  const [greetingLoaded, setGreetingLoaded] = useState(false);
  const [input, setInput] = useState(prefillText ?? '');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Fetch the agent's welcome config — same endpoint ChatView uses for
  // authed users. Falls back gracefully if the agent or config is missing.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const [agentRes, userRes] = await Promise.all([
          fetch(`/api/agents/${encodeURIComponent(username)}?greeting=1`),
          fetch(`/api/users/${encodeURIComponent(username)}`),
        ]);
        const agentData = agentRes.ok ? await agentRes.json() : null;
        const userData = userRes.ok ? await userRes.json() : null;
        if (cancelled) return;
        const c = agentData?.config;
        const u = userData?.user;
        setGreeting({
          displayName: u?.displayName || u?.username || undefined,
          avatarUrl: u?.avatarUrl || undefined,
          greetingMessage: c?.greetingMessage,
          greetingPrompt: c?.greetingPrompt || undefined,
          suggestedActions: c?.suggestedActions,
          heroImage: c?.greetingImageDesktop || c?.greetingImageMobile || undefined,
        });
      } catch {
        /* keep defaults */
      } finally {
        if (!cancelled) setGreetingLoaded(true);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [username]);

  const resizeTextarea = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, []);

  useLayoutEffect(() => {
    resizeTextarea();
  }, [input, resizeTextarea]);

  const submit = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      haptic('light');
      // Open the signup dialog (an explicit gesture). NOTE: the typed prompt
      // is no longer carried across signup — the post-signup clone-from
      // hand-off was retired with the move to guest accounts. FOLLOW-UP:
      // mint a guest carrying `trimmed` and start the chat in their workspace.
      await requireSignup({
        reason: 'send_message',
        sourceWorkspaceId: sourceWorkspaceId ?? null,
        pendingPrompt: trimmed,
        // Deliberate gesture — always open the dialog even if the
        // visitor previously dismissed a passive prompt.
        explicit: true,
      });
    },
    [requireSignup, sourceWorkspaceId],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void submit(input);
      }
    },
    [input, submit],
  );

  const handleSuggestedAction = useCallback(
    (raw: string) => {
      haptic('selection');
      const hashIdx = raw.indexOf('#');
      const prompt = hashIdx >= 0 ? raw.slice(hashIdx + 1) : raw;
      setInput(prompt);
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      });
    },
    [],
  );

  if (!greetingLoaded) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="2xl" tone="muted" />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col bg-background relative h-full"
      data-testid="anon-agent-chat-preview"
      data-anon-agent={username}
    >
      <div className="flex-1 relative min-h-0 overflow-y-auto">
        <div className="p-4 flex flex-col items-center justify-center text-center flex-1 min-h-full w-full">
          {greeting?.heroImage ? (
            <div className="w-full max-w-2xl px-4 mb-5">
              <div className="rounded-2xl overflow-hidden">
                <Image
                  src={greeting.heroImage}
                  alt=""
                  width={800}
                  height={420}
                  unoptimized
                  className="w-full h-auto object-cover"
                  sizes="(max-width: 768px) 100vw, 672px"
                />
              </div>
            </div>
          ) : (
            <Avatar
              seed={username}
              type="agent"
              src={greeting?.avatarUrl}
              className="h-[4.25rem] w-[4.25rem] mb-5"
            />
          )}
          <div className="max-w-[350px]">
            {greeting?.greetingMessage ? (
              <p className="text-sm text-muted-foreground mb-4">
                {greeting.greetingMessage}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground mb-4">
                Hey, I&apos;m {greeting?.displayName || username}. Try me out — what can I help with?
              </p>
            )}
          </div>
          {greeting?.greetingPrompt && (
            <p className="text-sm font-medium text-purple-500 mb-8">
              {greeting.greetingPrompt}
            </p>
          )}
          {greeting?.suggestedActions && greeting.suggestedActions.length > 0 && (
            <div className="flex flex-col items-center gap-2.5">
              {greeting.suggestedActions.map((raw, i) => {
                const hashIdx = raw.indexOf('#');
                const label = hashIdx >= 0 ? raw.slice(0, hashIdx) : raw;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleSuggestedAction(raw)}
                    className="px-6 py-2 rounded-full border border-border text-sm hover:bg-accent transition-colors"
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="shrink-0 border-t border-border bg-background p-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('anonChat.askAnything')}
            rows={1}
            className={cn(
              'flex-1 resize-none rounded-2xl border border-border bg-background',
              'px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring',
              'max-h-60',
            )}
          />
          <button
            type="button"
            onClick={() => void submit(input)}
            disabled={!input.trim()}
            aria-label={t('anonChat.send')}
            className={cn(
              'h-10 w-10 rounded-full flex items-center justify-center',
              'bg-primary text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed',
              'hover:opacity-90 transition-opacity',
            )}
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground text-center">
          {t('anonChat.signUpNote')}
        </p>
      </div>
    </div>
  );
}
